import axiosInstance from '@/core/api'
import {
	CreateBankAccountRequest,
	CreateBankAccountResponse,
	CreateBankDepositTransactionRequest,
	CreateBankDepositTransactionResponse,
	CreateBankWithdrawalTransactionRequest,
	CreateBankWithdrawalTransactionResponse,
	CreatePaymentGatewayRequest,
	CreatePaymentGatewayResponse,
	GetBankAccountByIdResponse,
	GetBankAccountsRequest,
	GetBankProviderResponse,
	GetDepositTransactionByIdResponse,
	GetOperatorDepositTransactionsRequest,
	GetOperatorDepositTransactionsResponse,
	GetOperatorWithdrawalTransactionsRequest,
	GetOperatorWithdrawalTransactionsResponse,
	GetPaymentGateProviderResponse,
	GetPaymentGatewayByIdResponse,
	GetPaymentGateWayRequest,
	GetPaymentGateWayResponse,
	AvailableChannelsResponse,
	ChannelCountsResponse,
	GetPaymentGatewayConstraintsResponse,
	UpdateBankAccountRequest,
	UpdateBankAccountResponse,
	UpdatePaymentGatewayRequest,
	UpdatePaymentGatewayResponse,
	GetBankAccountResponse,
	ProviderMaintenanceListResponse,
	CreateMaintenanceRequest,
	CreateMaintenanceResponse,
	PaymentGatewayAnalyticsResponse,
	GetPaymentGatewayAnalyticsParams,
	OperatorDepositTransactionById,
} from '../../type'

export const getBankAccount = async (
	payload: GetBankAccountsRequest,
): Promise<GetBankAccountResponse> => {
	const response = await axiosInstance.get<GetBankAccountResponse>(
		'/admin/bank-accounts/get-bank-account',
		{
			params: payload,
		},
	)
	return response.data
}

export const getBankAccountById = async (
	id: string,
): Promise<GetBankAccountByIdResponse> => {
	const response = await axiosInstance.get<GetBankAccountByIdResponse>(
		`/admin/bank-accounts/get-bank-account/${id}`,
	)
	return response.data
}

export const createBankAccount = async (
	payload: CreateBankAccountRequest,
): Promise<CreateBankAccountResponse> => {
	const response = await axiosInstance.post<CreateBankAccountResponse>(
		'/admin/bank-accounts/create-bank-account',
		payload,
	)
	return response.data
}

export const updateBankAccount = async (
	id: string,
	payload: UpdateBankAccountRequest,
): Promise<UpdateBankAccountResponse> => {
	const response = await axiosInstance.patch<UpdateBankAccountResponse>(
		`/admin/bank-accounts/update-bank-account/${id}`,
		payload,
	)
	return response.data
}

export const deleteBankAccount = async (id: string): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/bank-accounts/delete-bank-account/${id}`,
	)
	return response.data
}

export const getPaymentGateway = async (
	payload: GetPaymentGateWayRequest,
): Promise<GetPaymentGateWayResponse> => {
	const response = await axiosInstance.get<GetPaymentGateWayResponse>(
		'/admin/payment-gateway',
		{
			params: payload,
		},
	)
	return response.data
}

export const getAvailableChannels =
	async (): Promise<AvailableChannelsResponse> => {
		const response = await axiosInstance.get<AvailableChannelsResponse>(
			'/admin/payment-gateway/available-channels',
		)
		return response.data
	}

export const getChannelCounts = async (): Promise<ChannelCountsResponse> => {
	const response = await axiosInstance.get<ChannelCountsResponse>(
		'/admin/payment-gateway/channel-counts',
	)
	return response.data
}

export const getPaymentGatewayById = async (
	id: string,
): Promise<GetPaymentGatewayByIdResponse> => {
	const response = await axiosInstance.get<GetPaymentGatewayByIdResponse>(
		`/admin/payment-gateway/${id}`,
	)
	return response.data
}

export const createPaymentGateway = async (
	payload: CreatePaymentGatewayRequest,
): Promise<CreatePaymentGatewayResponse> => {
	const response = await axiosInstance.post<CreatePaymentGatewayResponse>(
		'/admin/payment-gateway',
		payload,
	)
	return response.data
}

export const updatePaymentGateway = async (
	payload: UpdatePaymentGatewayRequest,
): Promise<UpdatePaymentGatewayResponse> => {
	const response = await axiosInstance.put<UpdatePaymentGatewayResponse>(
		`/admin/payment-gateway`,
		payload,
	)
	return response.data
}

export const deletePaymentGateway = async (id: string): Promise<void> => {
	const response = await axiosInstance.delete<void>(
		`/admin/payment-gateway/${id}`,
	)
	return response.data
}

export const getBankProviders = async (): Promise<
	GetBankProviderResponse[]
> => {
	const response = await axiosInstance.get<GetBankProviderResponse[]>(
		'/admin/bank-accounts/providers',
	)
	return response.data
}

export const getPaymentGatewayProviders = async (): Promise<
	GetPaymentGateProviderResponse[]
> => {
	const response = await axiosInstance.get<GetPaymentGateProviderResponse[]>(
		'/admin/payment-gateway/provider',
	)

	return response.data
}

export const getPaymentGatewayConstraints =
	async (): Promise<GetPaymentGatewayConstraintsResponse> => {
		const response =
			await axiosInstance.get<GetPaymentGatewayConstraintsResponse>(
				'/admin/payment-gateway/constraints',
			)

		return response.data
	}

export const syncPaymentGateway = async (): Promise<void> => {
	const response = await axiosInstance.post<void>('/admin/payment-gateway/sync')
	return response.data
}

export const getMaintenances =
	async (): Promise<ProviderMaintenanceListResponse> => {
		const response = await axiosInstance.get<ProviderMaintenanceListResponse>(
			'/admin/payment-gateway/maintenances',
		)
		return response.data
	}

export const getMaintenancesAll =
	async (): Promise<ProviderMaintenanceListResponse> => {
		const response = await axiosInstance.get<ProviderMaintenanceListResponse>(
			'/admin/payment-gateway/maintenances/all',
		)
		return response.data
	}

const MAINTENANCE_KEY_HEADER = 'x-bank-maintenance-key'

export const createMaintenance = async (
	payload: CreateMaintenanceRequest,
	maintenanceKey: string,
): Promise<CreateMaintenanceResponse> => {
	const response = await axiosInstance.post<CreateMaintenanceResponse>(
		'/admin/payment-gateway/maintenances',
		payload,
		{
			headers: { [MAINTENANCE_KEY_HEADER]: maintenanceKey },
		},
	)
	return response.data
}

export const rejectMaintenance = async (
	id: string,
	maintenanceKey: string,
): Promise<{ id: string }> => {
	const response = await axiosInstance.post<{ id: string }>(
		`/admin/payment-gateway/maintenances/${id}/reject`,
		undefined,
		{
			headers: { [MAINTENANCE_KEY_HEADER]: maintenanceKey },
		},
	)
	return response.data
}

export const syncBank = async (): Promise<void> => {
	const response = await axiosInstance.post<void>(
		'/admin/bank-accounts/sync-bank-accounts',
	)
	return response.data
}

export const createBankDepositTransaction = async (
	payload: CreateBankDepositTransactionRequest,
): Promise<CreateBankDepositTransactionResponse> => {
	const response =
		await axiosInstance.post<CreateBankDepositTransactionResponse>(
			'/admin/bank-transactions/deposits/operator',
			payload,
		)
	return response.data
}

export const getAdminSlipPresignUrl = async (
	fileExtension: string = 'jpg',
): Promise<{ presignedUrl: string; fileUrl: string }> => {
	const response = await axiosInstance.post<{
		presignedUrl: string
		fileUrl: string
	}>('/admin/file/get-slip-presign-url', { fileExtension })
	return response.data
}

export const uploadOperatorDepositSlip = async (
	id: string,
	imageUrl: string,
): Promise<{ success: boolean; message: string }> => {
	const response = await axiosInstance.post<{
		success: boolean
		message: string
	}>(`/admin/bank-transactions/deposits/operator/${id}/upload-slip`, {
		imageUrl,
	})
	return response.data
}

export const createBankWithdrawalTransaction = async (
	payload: CreateBankWithdrawalTransactionRequest,
): Promise<CreateBankWithdrawalTransactionResponse> => {
	const response =
		await axiosInstance.post<CreateBankWithdrawalTransactionResponse>(
			'/admin/bank-transactions/withdrawals/operator',
			payload,
		)
	return response.data
}

export const getDepositTransactionById = async (
	id: string,
): Promise<GetDepositTransactionByIdResponse> => {
	const response = await axiosInstance.get<GetDepositTransactionByIdResponse>(
		`/admin/bank-transactions/deposits/operator/${id}`,
	)
	return response.data
}

export const getOperatorDepositTransactionById = async (
	id: string,
): Promise<OperatorDepositTransactionById> => {
	const response = await axiosInstance.get<OperatorDepositTransactionById>(
		`/admin/bank-transactions/deposits/operator/${id}`,
	)
	return response.data
}

export const getOperatorDepositTransactions = async (
	payload: GetOperatorDepositTransactionsRequest,
): Promise<GetOperatorDepositTransactionsResponse> => {
	const response =
		await axiosInstance.get<GetOperatorDepositTransactionsResponse>(
			'/admin/bank-transactions/deposits/operator',
			{
				params: payload,
			},
		)
	return response.data
}

export const getOperatorWithdrawalTransactions = async (
	payload: GetOperatorWithdrawalTransactionsRequest,
): Promise<GetOperatorWithdrawalTransactionsResponse> => {
	const response =
		await axiosInstance.get<GetOperatorWithdrawalTransactionsResponse>(
			'/admin/bank-transactions/withdrawals/operator',
			{
				params: payload,
			},
		)
	return response.data
}

export const getPaymentGatewayAnalytics = async (
	params?: GetPaymentGatewayAnalyticsParams,
): Promise<PaymentGatewayAnalyticsResponse> => {
	const response = await axiosInstance.get<PaymentGatewayAnalyticsResponse>(
		'/admin/payment-gateway/analytics',
		{ params },
	)
	return response.data
}
